import { useEventsFeed } from "./useEventsFeed.ts";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "../Accounts.tsx";
import { Box, CircularProgress, Typography } from "@mui/material";
import { EventCard } from "./EventCard.tsx";
import Semantic from "../../../../../eventer/Semantic.ts";
import EmptyEventsFeedPage from "./EmptyEventsFeedPage.tsx";
import { useBrandingTitleSuffix } from "../../../../../appfw/ui/branding/BrandingControl.tsx";
import { useRefsReady } from "../../../../../appfw/react/use-refs-ready.tsx";

export default function EventsFeed() {

    const account = useAccount()

    const eventsFeed = useEventsFeed({ pod: account.podName })
    const pod = eventsFeed.pod

    const containerRef = useRef<HTMLDivElement>(null)
    const [firstEventsRendering, setFirstEventsRendering] = useState(true)

    const refsReady = useRefsReady([containerRef])

    const handleScroll = useCallback(() => {

        const container = containerRef.current
        if (!container) {
            console.log("scroll: handleScroll ==> no container")
            return;
        }

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        const nearTop = scrollTop < 200

        console.log("scroll: handleScroll", firstEventsRendering, nearTop, eventsFeed.hasPrev, eventsFeed.isLoadingPrev())

        if (!firstEventsRendering && nearTop && eventsFeed.hasPrev && !eventsFeed.isLoadingPrev()) {
            console.log("scroll: 🔁 Near top → loading prev")

            const prevScrollHeight = container.scrollHeight;
            eventsFeed.loadPrev().then(() => {
                requestAnimationFrame(() => {
                    const newScrollHeight = container.scrollHeight;
                    const diff = newScrollHeight - prevScrollHeight;
                    container.scrollTop = scrollTop + diff;

                    console.log("scroll: 🔁 scrollTop corrected by", diff);
                })
            })
        }

        const nearBottom = scrollTop + clientHeight >= scrollHeight - 200;
        if (nearBottom && eventsFeed.hasNext && !eventsFeed.isLoadingNext()) {
            console.log("scroll: 🔁 Near bottom → loading next");
            eventsFeed.loadNext()
        }
    }, [refsReady, eventsFeed, firstEventsRendering])

    const scrollToEvent = (eventUri: string) => {
        const el = document.querySelector(`[data-anchor-id="${eventUri}"]`) as HTMLElement | null;
        el?.scrollIntoView({
            block: 'start',
            // behavior: 'smooth',
        })
    }

    useBrandingTitleSuffix("Veranstaltungen")

    useEffect(() => {
        const container = containerRef.current
        if (!container) return;

        container.addEventListener('scroll', handleScroll)

        return () => {
            container.removeEventListener('scroll', handleScroll)
        }
    }, [refsReady, handleScroll])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return;

        eventsFeed.refresh().then(handleScroll)
    }, [refsReady, firstEventsRendering])

    useEffect(() => {

        console.log("scroll: firstRendering effect", containerRef.current, firstEventsRendering, eventsFeed.events?.length)
        if (!containerRef.current || !firstEventsRendering || eventsFeed.events === undefined) {
            return
        }

        const now = new Date()
        const todayTimestamp = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            0, 0, 0, 0,
        ).getTime()
        let event = eventsFeed.events.find((event) => {
            let startDate = event.getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri)?.getTime() ?? 0
            if (startDate >= todayTimestamp) {
                return event
            }
        })

        let eventUri = event?.subject

        console.log("scroll: adjust scrollheight to event", eventUri)

        if (eventUri) {

            requestAnimationFrame(() => {

                scrollToEvent(eventUri)

                setTimeout(() => {

                    // ensure scroll is "more" correct, sizes (of event cards) might change
                    requestAnimationFrame(() => {
                        scrollToEvent(eventUri)
                    })

                    setFirstEventsRendering(false)
                }, 400)
            })
        } else {
            setFirstEventsRendering(false)
        }

    }, [refsReady, firstEventsRendering, eventsFeed.events])

    let isInitialLoading =
        eventsFeed.events === undefined ||
        (eventsFeed.events.length == 0 && eventsFeed.isLoading)

    if (pod === undefined || isInitialLoading) {
        return <Box
            sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <CircularProgress />
            <Typography variant="h6" sx={{ margin: '1rem' }}>Lade Veranstaltungen</Typography>
        </Box>
    }

    if (!eventsFeed.isLoading && eventsFeed.events.length === 0) {
        return <EmptyEventsFeedPage />
    }

    return (
        <>
            <Box
                ref={containerRef}
                sx={{
                    overflowY: 'scroll',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {
                    eventsFeed.events?.map((event) => {
                        return <EventCard pod={pod} event={event} key={event.subject} />
                    })
                }
            </Box>
        </>
    )
}